import { useCallback, useState } from 'react';
import { TFunction } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Section from '../../../componentsv2/Section';
import Table from '../../../componentsv2/Table';
import TableFilters from '../../../components/TableFilters';
import Typography from '../../../componentsv2/Typography';
import Chip from '../../../componentsv2/Chip';
import { useSnackbar } from '../../../context';
import { useInvoiceFinancingCompanies } from '../../../hooks/useCompanies';
import { formatDate } from '../../../utils/common';
import { invoiceFinancingCompaniesTableFilters } from '../../../constants/common';
import {
  InvoiceFinancingCompanyAdminResult,
  InvoiceFinancingCompanyAdminFilters,
} from '../../../types/companyUser';

const INITIAL_PAGE = 1;

export default function InvoiceFinancingCompanies({ t }: { t: TFunction }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [filters, setFilters] = useState<InvoiceFinancingCompanyAdminFilters>({
    company: '',
    ordering: '',
    status: [],
    deletedOnly: false,
    page: INITIAL_PAGE,
    pageSize: 10,
  });

  const handleError = useCallback(
    (errorCode?: string) => {
      showSnackbar('There was an error. Please try again later.', 'error');
    },
    [showSnackbar]
  );

  const { data: companiesData, isLoading } = useInvoiceFinancingCompanies(filters, handleError);

  const handleApplyFilters = (newFilters: any) => {
    setFilters({ ...newFilters, page: INITIAL_PAGE });
  };

  return (
    <Section sx={{ backgroundColor: '#fff', padding: '1em 2em' }}>
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <TableFilters
            showLabel={false}
            showFilteringButtons={false}
            filterOptions={invoiceFinancingCompaniesTableFilters}
            applyFilters={(filters: any) => handleApplyFilters(filters)}
            clearFilters={null}
            initialValues={filters}
          />
        </Grid>
        <Grid item xs={12}>
          <Table
            emptyMessageTitle={t(`admin:clientsPage.emptyMessageTitle`)}
            data={companiesData?.results ?? []}
            isLoading={isLoading}
            onClickRow={(row: InvoiceFinancingCompanyAdminResult) => {
              navigate(`/companies/${row.uuid}`);
            }}
            paginationSettings={{
              count: companiesData?.count ?? 0,
              showPages: true,
              handlePageChange: (_, newPage) => {
                setFilters((prevFilters) => ({ ...prevFilters, page: newPage + 1 }));
              },
              page: filters.page - 1,
              rowsPerPage: filters.pageSize,
            }}
            columns={[
              {
                field: 'uuid',
                headerName: t(`admin:clientsPage.columnOptions.companyId`),
              },
              {
                field: 'userName',
                headerName: t(`admin:clientsPage.columnOptions.user`),
              },
              {
                field: 'name',
                headerName: t(`admin:clientsPage.columnOptions.company`),
              },
              {
                field: 'country',
                headerName: t(`admin:clientsPage.columnOptions.country`),
                renderCell: (row: InvoiceFinancingCompanyAdminResult) => {
                  return <Typography>{t(`user:preapproval.location.options.${row.country}`)}</Typography>;
                },
              },
              {
                field: 'stage',
                headerName: t(`admin:clientsPage.columnOptions.stage`),
                renderCell: (row: InvoiceFinancingCompanyAdminResult) => {
                  return (
                    <Chip
                      label={t(`user:dashboard.stage.${row.stage}`)}
                      size="small"
                      sx={{ backgroundColor: '#E1FAED', color: '#11705F' }}
                    />
                  );
                },
              },
              {
                field: 'status',
                headerName: t(`admin:clientsPage.columnOptions.status`),
                renderCell: (row: InvoiceFinancingCompanyAdminResult) => {
                  return (
                    <Chip
                      label={t(`user:dashboard.status.${row.status}`)}
                      size="small"
                      sx={{ backgroundColor: '#E1FAED', color: '#11705F' }}
                    />
                  );
                },
              },
              {
                field: 'updatedAt',
                headerName: t(`admin:clientsPage.columnOptions.dateModified`),
                renderCell: (row: InvoiceFinancingCompanyAdminResult) => {
                  return (
                    <Typography>
                      {formatDate(new Date(row.updatedAt), 'en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                      })}
                    </Typography>
                  );
                },
              },
            ]}
          />
        </Grid>
      </Grid>
    </Section>
  );
}
